import { useEffect, useState } from 'react';
import styled from "@emotion/styled";
import { colors as themeColors } from 'src/theme';
import { Grid, InputLabel } from 'src/components';
import { mediaQueryMax, ValidationErrorStyle } from 'src/common';

const Option = styled.div((props: { 
  isSelected: boolean, 
  center: boolean, 
  colors: { background?: string, text?: string, backgroundSelected?: string, textSelected?: string },
  horizontal: boolean
 }) => ({
  color: props.isSelected ? props.colors.textSelected : props.colors.text,
  backgroundColor: props.isSelected ? props.colors.backgroundSelected : props.colors.background,
  // border: props.isSelected ? `2px solid ${colors.primary1}` : `2px solid ${colors.background6}`,
  cursor: 'pointer',
  padding: props.horizontal ? '10px 16px' : '10px 24px',
  borderRadius: '8px',
  flex: 1,
  zIndex: 1,
  fontSize: '16px',
  fontWeight: props.isSelected ? '700' : '400',
  textAlign: (props.center ? 'center' : 'left') as 'center' | 'left',
  lineHeight: '18px',
  
  [mediaQueryMax('mobile')]: {
    padding: '10px 24px',
    fontSize: '18px',
    lineHeight: '20px',
    textAlign: 'left' as 'left'
  },
}));

const InputLabelStyle = styled(InputLabel)({
  marginBottom: '8px',
});

const GridStyle = styled(Grid)({
  [mediaQueryMax('mobile')]: {
    gridTemplateColumns: '1fr',
  },
});

interface RadioOption {
  value: string;
  label: string;
}

interface RadioButtonGroupProps {
  style?: React.CSSProperties;
  options: RadioOption[];
  value?: string;
  onChange?: (selectedOption: string) => void;
  className?: string;
  label?: string;
  error?: string;
  horizontal?: boolean;
  gridColumns?: string; // grid columns ima smisla samo ako je horizontal false
  center?: boolean;
  colors?: {
    background?: string;
    text?: string;
    backgroundSelected?: string;
    textSelected?: string;
  },
  optional?: boolean;
}

export const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  style,
  options,
  value,
  onChange,
  className,
  label,
  error,
  gridColumns = `1fr`,
  horizontal = false,
  center = false,
  colors: customColors = {},
  optional = false
}) => {
  const colors = {
    background: themeColors.background6,
    text: themeColors.text2,
    backgroundSelected: themeColors.primary1,
    textSelected: themeColors.text7,
    ...customColors
  };

  const [selectedOption, setSelectedOption] = useState<string | undefined>(value);

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  // Funkcija za ažuriranje stanja
  const handleOptionChange = (optionValue: string) => {
    setSelectedOption(optionValue);
    if (onChange) onChange(optionValue); // Pozivamo onChange prop funkciju sa izabranom opcijom
  };

  const columns = horizontal ? `repeat(${options.length}, 1fr)` : gridColumns;

  return (
    <div>
      {label ? <InputLabelStyle optional={optional}>{label}</InputLabelStyle> : null}
      <GridStyle columns={columns} gap='4px' className={className}>
        {options.map((option) => (
          <Option
            key={option.value}
            onClick={() => handleOptionChange(option.value)}
            isSelected={selectedOption === option.value}
            center={center}
            colors={colors}
            horizontal={horizontal}
            style={horizontal ? {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...style
            } : {...style}}
          >
            {option.label}
          </Option>
        ))}
      </GridStyle>
      {error ? <ValidationErrorStyle>{error}</ValidationErrorStyle> : null}
    </div>
  );
};
